import { BlogPostDetailsForm } from "@/app/Blogs/Forms/BlogPostDetailsForm";
import { BlogPostContentForm } from "@/app/Blogs/Forms/BlogPostContentForm";
import { useGqlMutation, useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { updateBlogPostMutation } from "@/app/Blogs/GraphQL/blogPostMutations";
import { blogPostRecordQuery } from "@/app/Blogs/GraphQL/blogPostRecordQuery";
import { toFormikValidationSchema } from "@/lib/Zod/zod-formik-adapter";
import {
  blogPostDetailsValidation,
  blogPostContentValidation,
  type BlogPostDetailsInput,
  type BlogPostContentInput,
} from "@/app/Blogs/Validation/blogValidation";
import { blogPostRoute } from "@/app/Blogs/Routes/blogRoutes";
import { useTenant } from "@/app/Organisations/Hooks/useTenant";
import { RecordScreenFormCard } from "@/lib/Components/Screens/RecordScreen/Cards/RecordScreenFormCard";
import { RecordScreenCard } from "@/lib/Components/Screens/RecordScreen/Cards/RecordScreenCard";
import { BlogHeroImagePanel } from "@/app/Blogs/Components/BlogHeroImagePanel";
import { BlogPostDetailsView } from "@/app/Blogs/Components/BlogPostDetailsView";
import type { BlogPreviewMetadata } from "@/app/Blogs/Components/BlogPreviewIframe";

export function BlogPostEditScreen() {
  const { id } = blogPostRoute.useParams();
  const { data } = useGqlQuery(blogPostRecordQuery, { id });
  const { mutateAsync } = useGqlMutation(updateBlogPostMutation);
  const { isAdmin } = useTenant();

  const post = data?.blogPost;

  if (!post) {
    return <div className="p-6 text-zinc-500">Post not found.</div>;
  }

  const metadata: BlogPreviewMetadata = {
    title: post.title,
    excerpt: post.excerpt,
    hero_image_url: post.hero_image?.url ?? null,
    reading_time_minutes: post.reading_time_minutes,
    published_at: post.published_at,
    body_images: post.body_images,
    author: post.author
      ? {
          name: post.author.name,
          role: post.author.role,
          image: post.author.image ?? null,
        }
      : null,
  };

  return (
    <div className="space-y-12 py-6">
      <RecordScreenFormCard<BlogPostDetailsInput>
        title="Details"
        isEditable={isAdmin}
        validationSchema={toFormikValidationSchema(blogPostDetailsValidation)}
        onSubmit={async (values) => {
          await mutateAsync({ id: post.id, input: values });
        }}
        view={<BlogPostDetailsView post={post} />}
        initialValues={{
          slug: post.slug,
          locale: post.locale,
          title: post.title,
          excerpt: post.excerpt,
          blog_author_id: post.author?.id ?? null,
          audience: post.audience,
        }}
      >
        <BlogPostDetailsForm />
      </RecordScreenFormCard>

      <RecordScreenCard title="Hero image">
        <BlogHeroImagePanel
          postId={post.id}
          heroImage={post.hero_image ?? null}
          isEditable={isAdmin}
        />
      </RecordScreenCard>

      <RecordScreenFormCard<BlogPostContentInput>
        title="Content"
        isEditable={isAdmin}
        validationSchema={toFormikValidationSchema(blogPostContentValidation)}
        onSubmit={async (values) => {
          await mutateAsync({ id: post.id, input: values });
        }}
        initialValues={{ body_html: post.body_html }}
      >
        <div className="col-span-full">
          <BlogPostContentForm postId={post.id} metadata={metadata} />
        </div>
      </RecordScreenFormCard>
    </div>
  );
}
